import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatCurrencyAmount } from '../../../theme/tokens';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, DownloadIcon, MailIcon, EditIcon, PrinterIcon, CheckCircleIcon, XCircleIcon, MoreHorizontalIcon, ArchiveIcon, TrashIcon, UserIcon, PhoneIcon, MapPinIcon, CreditCardIcon, TagIcon, FileTextIcon, AlertTriangleIcon, ClockIcon, PackageIcon, TruckIcon, CalendarIcon, CheckIcon } from 'lucide-react';
import { Order, OrderStatus } from './Orders';
export function OrderDetail() {
  const {
    t
  } = useTranslation('shops');
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  // Mock data based on the ID or default
  const order: Order = {
    id: id || '220629-1e753',
    status: 'new',
    paymentStatus: 'paid',
    customer: {
      name: 'randy',
      email: 'randy@randy.com',
      address: 'randy',
      mobileNumber: '6596605522',
      additionalPhone: '1234',
      address2: '1234'
    },
    items: [{
      name: 'Kittens',
      quantity: 2,
      price: 111.0
    }],
    total: 222.0,
    date: '29/06/2022',
    time: '14:28',
    transaction: '62bc1b4be3c2250009f1e753',
    transactionStatus: 'confirmed',
    notes: 'Please deliver between 9 AM and 5 PM.',
    shopId: '1',
    shopName: 'Beach Shop'
  };
  const getStatusBadge = (status: OrderStatus) => {
    const styles = {
      new: 'bg-blue-50 text-blue-700 border-blue-200',
      'in-progress': 'bg-amber-50 text-amber-700 border-amber-200',
      open: 'bg-purple-50 text-purple-700 border-purple-200',
      completed: 'bg-green-50 text-green-700 border-green-200',
      cancelled: 'bg-red-50 text-red-700 border-red-200'
    };
    const labels = {
      new: 'New',
      'in-progress': 'In Progress',
      open: 'Open',
      completed: 'Completed',
      cancelled: 'Cancelled'
    };
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium border ${styles[status]}`} data-id="element-11955">
        {labels[status]}
      </span>;
  };
  const getPaymentMethod = (transaction?: string) => {
    if (!transaction) return 'Unknown payment method';
    if (transaction.startsWith('62')) return 'Credit Card';
    return 'Bank Transfer';
  };
  const getShippingStatus = (status: OrderStatus) => {
    switch (status) {
      case 'new':
        return 'Awaiting processing';
      case 'in-progress':
        return 'Processing';
      case 'open':
        return 'Ready for pickup';
      case 'completed':
        return 'Delivered';
      case 'cancelled':
        return 'Cancelled';
      default:
        return 'Unknown';
    }
  };
  const formatDate = (dateString: string) => {
    const parts = dateString.split('/');
    if (parts.length !== 3) return dateString;
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const day = parseInt(parts[0]);
    const month = parseInt(parts[1]) - 1;
    const year = parseInt(parts[2]);
    return `${day} ${months[month]} ${year}`;
  };
  return <div className="max-w-[1400px] mx-auto space-y-6" data-id="element-11956">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-11957">
        <div className="flex items-center gap-4" data-id="element-11958">
          <button onClick={() => navigate('/orders')} className="p-2 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors" data-id="element-11959">
            <ArrowLeftIcon size={18} data-id="element-11960" />
          </button>
          <div data-id="element-11961">
            <div className="flex items-center gap-3" data-id="element-11962">
              <h1 className="text-xl font-semibold text-gray-900" data-id="element-11963">
                {t('pages.OrderDetail.order')}
                {order.id}
              </h1>
              {getStatusBadge(order.status)}
            </div>
            <p className="text-sm text-gray-500 mt-0.5" data-id="element-11964">
              {order.customer.name}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2" data-id="element-11965">
          <button className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-medium transition-colors" data-id="element-11966">
            <MailIcon size={16} data-id="element-11967" />
            {t('pages.OrderDetail.send')}
          </button>
          <button className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-medium transition-colors" data-id="element-11968">
            <DownloadIcon size={16} data-id="element-11969" />
            {t('pages.OrderDetail.download')}
          </button>
          <button className="flex items-center gap-2 px-4 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-11970">
            <CheckCircleIcon size={16} data-id="element-11971" />
            {t('pages.OrderDetail.mark_fulfilled')}
          </button>
          <div className="relative" data-id="element-11972">
            <button onClick={() => setShowMoreMenu(!showMoreMenu)} aria-label="More actions" aria-expanded={showMoreMenu} className="p-2 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors" data-id="element-11973">
              <MoreHorizontalIcon size={18} aria-hidden="true" data-id="element-11974" />
            </button>

            {showMoreMenu && <>
                <div className="fixed inset-0 z-10" onClick={() => setShowMoreMenu(false)} data-id="element-11975" />

                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20" data-id="element-11976">
                  <button onClick={() => setShowMoreMenu(false)} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-11977">
                    <PrinterIcon size={16} className="mr-3 text-gray-400" data-id="element-11978" />
                    {t('pages.OrderDetail.print_order')}
                  </button>
                  <button onClick={() => setShowMoreMenu(false)} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-11979">
                    <EditIcon size={16} className="mr-3 text-gray-400" data-id="element-11980" />
                    {t('pages.OrderDetail.edit_order')}
                  </button>
                  <div className="border-t border-gray-100 my-1" data-id="element-11981" />
                  <button onClick={() => setShowMoreMenu(false)} className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50" data-id="element-11982">
                    <TrashIcon size={16} className="mr-3" data-id="element-11983" />
                    {t('pages.OrderDetail.cancel_order')}
                  </button>
                </div>
              </>}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-11984">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6" data-id="element-11985">
          {/* Order Items */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-11986">
            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center" data-id="element-11987">
              <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2" data-id="element-11988">
                <PackageIcon size={18} className="text-gray-500" data-id="element-11989" />
                {t('pages.OrderDetail.order_items')}
              </h2>
            </div>
            <div className="p-6" data-id="element-11990">
              <div className="space-y-4" data-id="element-11991">
                {order.items.map((item, index) => <div key={index} className="flex justify-between border-b border-gray-100 pb-4 last:border-0 last:pb-0" data-id="element-11992">
                    <div data-id="element-11993">
                      <div className="font-medium text-gray-900" data-id="element-11994">
                        {item.name}
                      </div>
                      <div className="text-sm text-gray-500 mt-1" data-id="element-11995">
                        {t('pages.OrderDetail.quantity')} {item.quantity} ×{' '}
                        {formatCurrencyAmount(item.price, {
                      fractionDigits: 2
                    })}
                      </div>
                    </div>
                    <div className="font-medium text-gray-900" data-id="element-11996">
                      {formatCurrencyAmount(item.price * item.quantity, {
                    fractionDigits: 2
                  })}
                    </div>
                  </div>)}
              </div>

              <div className="mt-6 border-t border-gray-100 pt-4 space-y-3" data-id="element-11997">
                <div className="flex justify-between text-sm" data-id="element-11998">
                  <span className="text-gray-500" data-id="element-11999">
                    {t('pages.OrderDetail.subtotal')}
                  </span>
                  <span className="text-gray-900 font-medium" data-id="element-12000">
                    {formatCurrencyAmount(order.total, {
                    fractionDigits: 2
                  })}
                  </span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-12001">
                  <span className="text-gray-500" data-id="element-12002">
                    {t('pages.OrderDetail.shipping')}
                  </span>
                  <span className="text-gray-900 font-medium" data-id="element-12003">
                    {formatCurrencyAmount(0, {
                    fractionDigits: 2
                  })}
                  </span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-12004">
                  <span className="text-gray-500" data-id="element-12005">
                    {t('pages.OrderDetail.tax')}
                  </span>
                  <span className="text-gray-900 font-medium" data-id="element-12006">
                    {formatCurrencyAmount(0, {
                    fractionDigits: 2
                  })}
                  </span>
                </div>
                <div className="flex justify-between text-base font-semibold pt-3 border-t border-gray-100" data-id="element-12007">
                  <span className="text-gray-900" data-id="element-12008">
                    {t('pages.OrderDetail.total')}
                  </span>
                  <span className="text-[#9F140F]" data-id="element-12009">
                    {formatCurrencyAmount(order.total, {
                    fractionDigits: 2
                  })}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Order Timeline */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-12010">
            <div className="px-6 py-4 border-b border-gray-100" data-id="element-12011">
              <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2" data-id="element-12012">
                <ClockIcon size={18} className="text-gray-500" data-id="element-12013" />
                {t('pages.OrderDetail.order_timeline')}
              </h2>
            </div>
            <div className="p-6" data-id="element-12014">
              <div className="space-y-6" data-id="element-12015">
                <div className="flex items-start" data-id="element-12016">
                  <div className="flex-shrink-0 h-6 w-6 rounded-full bg-green-100 flex items-center justify-center mt-0.5" data-id="element-12017">
                    <CheckIcon size={14} className="text-green-600" data-id="element-12018" />
                  </div>
                  <div className="ml-4" data-id="element-12019">
                    <p className="text-sm font-medium text-gray-900" data-id="element-12020">
                      {t('pages.OrderDetail.order_placed')}
                    </p>
                    <p className="text-xs text-gray-500 mt-1" data-id="element-12021">
                      {formatDate(order.date)} {t('pages.OrderDetail.at')}{' '}
                      {order.time}
                    </p>
                  </div>
                </div>

                {order.status === 'in-progress' || order.status === 'completed' ? <div className="flex items-start" data-id="element-12022">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-amber-100 flex items-center justify-center mt-0.5" data-id="element-12023">
                      <CheckIcon size={14} className="text-amber-600" data-id="element-12024" />
                    </div>
                    <div className="ml-4" data-id="element-12025">
                      <p className="text-sm font-medium text-gray-900" data-id="element-12026">
                        {t('pages.OrderDetail.processing_started')}
                      </p>
                      <p className="text-xs text-gray-500 mt-1" data-id="element-12027">
                        {t('pages.OrderDetail.order_is_being_processed')}
                      </p>
                    </div>
                  </div> : <div className="flex items-start" data-id="element-12028">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-gray-100 flex items-center justify-center mt-0.5" data-id="element-12029">
                      <span className="text-xs font-medium text-gray-500" data-id="element-12030">
                        2
                      </span>
                    </div>
                    <div className="ml-4" data-id="element-12031">
                      <p className="text-sm font-medium text-gray-500" data-id="element-12032">
                        {t('pages.OrderDetail.processing')}
                      </p>
                      <p className="text-xs text-gray-400 mt-1" data-id="element-12033">
                        {t('pages.OrderDetail.waiting_to_be_processed')}
                      </p>
                    </div>
                  </div>}

                {order.status === 'completed' ? <div className="flex items-start" data-id="element-12034">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-green-100 flex items-center justify-center mt-0.5" data-id="element-12035">
                      <CheckIcon size={14} className="text-green-600" data-id="element-12036" />
                    </div>
                    <div className="ml-4" data-id="element-12037">
                      <p className="text-sm font-medium text-gray-900" data-id="element-12038">
                        {t('components.getOrderStatusBadge.completed')}
                      </p>
                      <p className="text-xs text-gray-500 mt-1" data-id="element-12039">
                        {t('pages.OrderDetail.order_has_been_fulfilled')}
                      </p>
                    </div>
                  </div> : <div className="flex items-start" data-id="element-12040">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-gray-100 flex items-center justify-center mt-0.5" data-id="element-12041">
                      <span className="text-xs font-medium text-gray-500" data-id="element-12042">
                        3
                      </span>
                    </div>
                    <div className="ml-4" data-id="element-12043">
                      <p className="text-sm font-medium text-gray-500" data-id="element-12044">
                        {t('components.getOrderStatusBadge.completed')}
                      </p>
                      <p className="text-xs text-gray-400 mt-1" data-id="element-12045">
                        {t('pages.OrderDetail.waiting_to_be_completed')}
                      </p>
                    </div>
                  </div>}
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6" data-id="element-12046">
          {/* Customer Information */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-12047">
            <div className="px-6 py-4 border-b border-gray-100" data-id="element-12048">
              <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2" data-id="element-12049">
                <UserIcon size={18} className="text-gray-500" data-id="element-12050" />
                {t('pages.OrderDetail.customer_information')}
              </h2>
            </div>
            <div className="p-6 space-y-4" data-id="element-12051">
              <div className="flex items-center gap-3 mb-2" data-id="element-12052">
                <div className="w-10 h-10 bg-[#E2231A]/10 rounded-full flex items-center justify-center text-[#9F140F] font-semibold" data-id="element-12053">
                  {order.customer.name.charAt(0).toUpperCase()}
                </div>
                <div data-id="element-12054">
                  <div className="font-medium text-gray-900" data-id="element-12055">
                    {order.customer.name}
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-12056">
                    {t('pages.OrderDetail.customer')}
                    {order.id.split('-')[0]}
                  </div>
                </div>
              </div>

              <div className="space-y-3 pt-2 border-t border-gray-100" data-id="element-12057">
                <div className="flex items-start gap-3" data-id="element-12058">
                  <MailIcon size={16} className="text-gray-400 mt-0.5" data-id="element-12059" />
                  <div data-id="element-12060">
                    <div className="text-sm text-gray-900" data-id="element-12061">
                      {order.customer.email}
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-12062">
                      {t('pages.OrderDetail.email')}
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-12063">
                  <PhoneIcon size={16} className="text-gray-400 mt-0.5" data-id="element-12064" />
                  <div data-id="element-12065">
                    <div className="text-sm text-gray-900" data-id="element-12066">
                      {order.customer.mobileNumber}
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-12067">
                      {t('pages.OrderDetail.mobile')}
                    </div>
                  </div>
                </div>
                {order.customer.additionalPhone && <div className="flex items-start gap-3" data-id="element-12068">
                    <PhoneIcon size={16} className="text-gray-400 mt-0.5" data-id="element-12069" />
                    <div data-id="element-12070">
                      <div className="text-sm text-gray-900" data-id="element-12071">
                        {order.customer.additionalPhone}
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-12072">
                        {t('pages.OrderDetail.additional_phone')}
                      </div>
                    </div>
                  </div>}
                <div className="flex items-start gap-3" data-id="element-12073">
                  <MapPinIcon size={16} className="text-gray-400 mt-0.5" data-id="element-12074" />
                  <div data-id="element-12075">
                    <div className="text-sm text-gray-900" data-id="element-12076">
                      {order.customer.address}
                    </div>
                    {order.customer.address2 && <div className="text-sm text-gray-900 mt-0.5" data-id="element-12077">
                        {order.customer.address2}
                      </div>}
                    <div className="text-xs text-gray-500 mt-0.5" data-id="element-12078">
                      {t('pages.OrderDetail.shipping_address')}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Information */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-12079">
            <div className="px-6 py-4 border-b border-gray-100" data-id="element-12080">
              <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2" data-id="element-12081">
                <CreditCardIcon size={18} className="text-gray-500" data-id="element-12082" />
                {t('pages.OrderDetail.payment_information')}
              </h2>
            </div>
            <div className="p-6 space-y-4" data-id="element-12083">
              <div className="flex items-start gap-3" data-id="element-12084">
                <CreditCardIcon size={16} className="text-gray-400 mt-0.5" data-id="element-12085" />
                <div data-id="element-12086">
                  <div className="text-sm text-gray-900" data-id="element-12087">
                    {getPaymentMethod(order.transaction)}
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-12088">
                    {t('pages.OrderDetail.payment_method')}
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3" data-id="element-12089">
                <TagIcon size={16} className="text-gray-400 mt-0.5" data-id="element-12090" />
                <div data-id="element-12091">
                  <div className="text-sm text-gray-900 capitalize" data-id="element-12092">
                    {order.paymentStatus}
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-12093">
                    {t('pages.OrderDetail.payment_status')}
                  </div>
                </div>
              </div>
              {order.transaction && <div className="flex items-start gap-3" data-id="element-12094">
                  <FileTextIcon size={16} className="text-gray-400 mt-0.5" data-id="element-12095" />
                  <div data-id="element-12096">
                    <div className="text-sm text-gray-900 break-all" data-id="element-12097">
                      {order.transaction}
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-12098">
                      {t('pages.OrderDetail.transaction_id')}
                    </div>
                  </div>
                </div>}
              {order.transactionStatus && <div className="flex items-start gap-3" data-id="element-12099">
                  {order.transactionStatus === 'confirmed' ? <CheckCircleIcon size={16} className="text-green-500 mt-0.5" data-id="element-12100" /> : order.transactionStatus === 'failed' ? <AlertTriangleIcon size={16} className="text-red-500 mt-0.5" data-id="element-12101" /> : <ClockIcon size={16} className="text-amber-500 mt-0.5" data-id="element-12102" />}
                  <div data-id="element-12103">
                    <div className="text-sm text-gray-900 capitalize" data-id="element-12104">
                      {order.transactionStatus}
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-12105">
                      {t('pages.OrderDetail.transaction_status')}
                    </div>
                  </div>
                </div>}
            </div>
          </div>

          {/* Shipping Status */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-12106">
            <div className="px-6 py-4 border-b border-gray-100" data-id="element-12107">
              <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2" data-id="element-12108">
                <TruckIcon size={18} className="text-gray-500" data-id="element-12109" />
                {t('pages.OrderDetail.shipping_status')}
              </h2>
            </div>
            <div className="p-6" data-id="element-12110">
              <div className="text-sm font-medium text-gray-900 mb-4" data-id="element-12111">
                {getShippingStatus(order.status)}
              </div>
              {order.notes && <div className="bg-gray-50 rounded-lg p-3 border border-gray-100" data-id="element-12112">
                  <div className="text-xs font-medium text-gray-700 mb-1" data-id="element-12113">
                    {t('pages.OrderDetail.customer_notes')}
                  </div>
                  <p className="text-sm text-gray-600" data-id="element-12114">{order.notes}</p>
                </div>}
            </div>
          </div>
        </div>
      </div>
    </div>;
}